/**
 * @name ContentRevisionView
 * @description Displays an accordion of the ContentRevisions within a Module.
 *  Each ContentRevision contains a list of RuleInstances.
 */

import React, { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SegmentIcon from '@mui/icons-material/Segment';
import { AppState } from "../../../redux/store";
import { getContentById } from "../../../redux/actions/RuleUnit";
import { ContentRevision } from "../../../types/models/ContentRevision";
import { RuleUnitState } from "../../../types/models/RuleUnitState";
import { RuleInstance } from "../../../types/models/RuleInstance";
import { SkeletonRuleItem } from "../../../components/RuleInstanceListSkeleton";
import { theme } from "../../../themes/theme";
import RuleInstanceList from "./RuleInstanceList";
import { useRules } from "../../../hooks/useRules";
import { useNavigate } from "react-router-dom";

interface ContentRevisionViewProps {
  contentRevision: ContentRevision | undefined;
  forDocumentDisplay: boolean;
  isExpanded?: boolean;
}

const ContentRevisionView: React.FC<ContentRevisionViewProps> = (prop) => {
  const { targetItem , searchTargetItem} = useRules();
  const navigate = useNavigate();
  const [expanded, setExpanded] = React.useState<boolean>(false);
  const [contentRevision, setContentRevision] = React.useState<ContentRevision | undefined>(undefined);
  const [isFirstLoad, setIsFirstLoad] = React.useState<boolean>(true);
  const ruleUnitState = useSelector<AppState, RuleUnitState>((state) => state.ruleUnit);
  const dispatch = useDispatch();

  const contentRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    try {
      if (!searchTargetItem || !contentRevision) return;

      // For module targets, check the content revision ID
      if (searchTargetItem.type === "module") {
        // Handle the UUID- prefix in moduleId
        const targetId = searchTargetItem.id;
        const contentId = contentRevision.ContentRevisionId;

        // Compare with and without UUID- prefix for flexibility
        const isMatch =
          contentId === targetId ||
          `UUID-${contentId}` === targetId ||
          contentId === targetId.replace("UUID-", "");

        if (isMatch && contentRef.current) {
          contentRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }

      // For rule targets, this will be handled in RuleInstanceList component
    } catch (error) {
      console.error("Scroll error:", error);
    }
  }, [searchTargetItem, contentRevision]);

  useEffect(() => {
    // First, check if we need to expand this accordion
    const shouldExpand = () => {
      if (!searchTargetItem || !contentRevision) return false;
      
      if (searchTargetItem.type === 'module') {
        const targetId = searchTargetItem.id;
        const contentId = contentRevision.ContentRevisionId;
        
        // Compare with and without UUID- prefix
        return contentId === targetId || 
               `UUID-${contentId}` === targetId ||
               contentId === targetId.replace('UUID-', '');
      }
      
      if (searchTargetItem.type === 'rule') {
        // Check if any rule in this content matches the target rule ID
        return contentRevision.RuleInstances?.some(rule => {
          const ruleId = rule.RuleId;
          const targetId = searchTargetItem.id;
          
          return ruleId === targetId || 
                 `UUID-${ruleId}` === targetId ||
                 ruleId === targetId.replace('UUID-', '');
        }) || false;
      }
      
      return false;
    };
    
    if (shouldExpand()) {
      setExpanded(true);
      
      setTimeout(() => {
        handleScroll();
      }, 300);
    }
  }, [searchTargetItem, contentRevision, handleScroll]);
  
  const matchesTarget = useCallback(() => {
    // If no target or no content, nothing should expand
    if (!targetItem || !targetItem.id || !contentRevision) return false;
    
    // For module type, match by exact content revision ID
    if (targetItem.type === 'module' && 
        contentRevision.ContentRevisionId === targetItem.id) {
      return true;
    }
    
    // For rule type, match by content ID or rule ID
    if (targetItem.type === 'rule') {
      // If we have a contentId in the target, check for exact match
      if (targetItem.contentId && 
          contentRevision.ContentRevisionId === targetItem.contentId) {
        return true;
      }
      
      // Otherwise check if any rule in this content matches target rule ID
      if (contentRevision.RuleInstances?.some(rule => rule.RuleId === targetItem.id)) {
        return true;
      }
    }
    
    return false;
  }, [targetItem, contentRevision]);
  
  // Initial data loading
  useEffect(() => {
    const fetchData = async () => {
      const targetDetails = prop.forDocumentDisplay 
        ? ruleUnitState.baseSupportDocumentContentRevisionDetails 
        : ruleUnitState.baseContentRevisionDetails;
        
      if (targetDetails?.some(content => 
          content.ContentRevisionId === prop.contentRevision?.ContentRevisionId)) {
        setCurrentContentRevision();
      } else {
        // Get content details
        dispatch(getContentById(prop.contentRevision?.ContentRevisionId ?? "", () => {
          setCurrentContentRevision();
        }));
      }
    };
    
    fetchData();
  }, []);
  
  useEffect(() => {
    // First check for expansion
    if (prop.isExpanded !== undefined) {
      setExpanded(prop.isExpanded);
      return;
    }
    
    // Then check if content matches target
    if (contentRevision && targetItem && matchesTarget()) {
      setExpanded(true);
    }
  }, [contentRevision, targetItem, matchesTarget, prop.isExpanded]);
  
  // Scroll to target if matches
  useEffect(() => {
    if (expanded && matchesTarget() && contentRef.current) {
      setTimeout(() => {
        contentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [expanded, matchesTarget]);
  
  const setCurrentContentRevision = () => {
    const targetDetails = prop.forDocumentDisplay 
      ? ruleUnitState.supportDocumentContentRevisionDetails 
      : ruleUnitState.contentRevisionDetails;
      
    const revision = targetDetails?.find(
      content => content.ContentRevisionId === prop.contentRevision?.ContentRevisionId
    );
    
    if (revision) {
      setContentRevision(revision);
    }
  };
  
  // Update content when redux state changes
  useEffect(() => {
    setIsFirstLoad(false);
    setCurrentContentRevision();
  }, [
    ruleUnitState.contentRevisionDetails, 
    ruleUnitState.supportDocumentContentRevisionDetails
  ]);
  
  const handleChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
  };
  
  const onViewRuleDetail = (selectedRule: RuleInstance) => {
    navigate(`/rules/${selectedRule.RuleId}`);
  };

  if (contentRevision || isFirstLoad) {
    return (
      <>
        <Accordion
          ref={contentRef}
          data-element="ContentRevisionView"
          data-content-id={prop.contentRevision?.ContentRevisionId}
          disableGutters={true}
          sx={{
            position: "relative",
            boxShadow: "none",
            borderBottom: (theme) => `solid 1px ${theme.palette.grey[200]}`,
            mb: "-1px",
            "&:last-of-type": {
              borderRadius: 0,
            },
            ...(matchesTarget() && {
              backgroundColor: 'rgba(25, 118, 210, 0.04)'
            })
          }}
          key={prop.contentRevision?.ContentRevisionId}
          expanded={expanded !== false}
          onChange={handleChange}>
          <AccordionSummary
            sx={{
              "& .MuiSvgIcon-root": {
                color: (theme) => theme.palette.grey[500],
              },
              "&.Mui-expanded": {
                borderBottom: (theme) => `solid 1px ${theme.palette.grey[200]}`,
              },
              paddingX: 1,
              ml: prop.forDocumentDisplay ? 0 : 1.5
            }}
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1bh-content"
            id="panel1bh-header"
          >
            {contentRevision ? (
              <>
                <SegmentIcon sx={{ mr: 1 }}></SegmentIcon>
                <Typography sx={{ width: '70%', flexShrink: 0, color: theme.palette.primary.main }}>
                  {contentRevision?.DisplayName ?? ""}
                </Typography>
                <Typography sx={{ color: theme.palette.primary.main, ml: "auto", fontSize: 15, textAlign: "center" }}>
                  {contentRevision.RuleInstances?.length + " items"}
                </Typography>
              </>
            ) : (
              <SkeletonRuleItem />
            )}
          </AccordionSummary>
          <AccordionDetails
            sx={{ p: 0, ml: prop.forDocumentDisplay ? 0 : 1 }}
          >
            <RuleInstanceList
              contentRevisionId={contentRevision?.ContentRevisionId}
              ruleInstances={contentRevision?.RuleInstances ?? undefined}
              onViewRuleDetail={onViewRuleDetail}
              targetItem={targetItem}
              searchTargetItem={searchTargetItem}
            />
          </AccordionDetails>
        </Accordion >
      </>
    );
  } else {
    // Empty searched result
    return <Box />
  }
};

export default ContentRevisionView;
